import React, { useContext } from "react";
import Row from "@amzn/meridian/row";
import { RouteContext } from "./context/RouteContextProvider";
import { OutboundFilterContext } from "./context/OutboundFilterContext";
import {
  ASSIGNABLE,
  DEPARTED,
  evaluateRouteAtRiskCount,
  getRouteCountByStatus,
  getRouteFilterByMetric,
  NOT_READY,
  PENDING_DEPART,
  PENDING_PICK,
  PENDING_STAGE,
  ROUTES_AT_RISK,
} from "./RouteStatusManager";
import ActionableMetricView from "./ActionableMetricView";
import { injectIntl } from "react-intl";
import Box from "@amzn/meridian/box";
import MetricViewV2 from "./MetricViewV2";
import {
  getRouteCodeListForStow,
  getTargetTabByMetric,
  isARorSSD,
} from "./util/Util";
import { OutboundConfigContext } from "./context/OutboundConfigContext";
import { CapabilityContext } from "../overview/context/CapabilityContext";
import Column from "@amzn/meridian/column";
import RefreshTimer from "../RefreshTimer";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import Responsive from "@amzn/meridian/responsive";
import { SMALL } from "../common/constant/BreakPoints";
import ScrollableElementWrapper from "../CommonModels/ScrollableElementWrapper";
import {
  ASSIGNED,
  IN_PROGRESS,
  INTERIM_STAGED,
  STAGED,
  STOWED,
} from "../outboundSSD/util/Constants";
import { isSSD } from "../outboundSSD/util/Util";

const OutboundOverviewAMZLView = (props) => {
  const { spacingInset_shorthand4, isMobile } = useDeviceLayout();
  const { viewModel } = useContext(RouteContext);
  const { viewModelF } = useContext(OutboundFilterContext);
  const { outboundConfigModel } = useContext(OutboundConfigContext);
  const { capability } = useContext(CapabilityContext);
  const isStationARorSSD = isARorSSD(capability.data);
  const isStationTabularSSD =
    isSSD(capability.data) &&
    outboundConfigModel.stationConfig.tabularGridViewSSDEnabled;
  const isDrsPickStageVisibilityEnabled =
    outboundConfigModel.stationConfig.drsPickStageVisibilityEnabled;
  const isAmxlStation = outboundConfigModel.stationConfig.amxl;
  const isSSDMMOTEnabled = outboundConfigModel.stationConfig.ssdmmotenabled;
  const isSTAGWorkflowEnabled = outboundConfigModel.stationConfig.stagenabled;
  const isOverviewMetricEnrichmentEnabled =
    outboundConfigModel.stationConfig.overviewMetricEnrichmentEnabled;
  const isAtRiskMetricEnrichmentEnabled =
    outboundConfigModel.stationConfig.atRiskMetricEnrichmentEnabled;

  let routeList = viewModel.routeList;

  const filterCluster = (route) => {
    if (viewModelF.cluster.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.cluster.forEach((cluster) => {
      if (route.routeCode.includes(cluster)) {
        result = true;
      }
    });
    return result;
  };
  const filterType = (route) => {
    if (viewModelF.driverType.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.driverType.forEach((driverType) => {
      if (route.routeType.includes(driverType)) {
        result = true;
      }
    });
    return result;
  };
  const filterDeparture = (route) => {
    if (viewModelF.departure.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.departure.forEach((departure) => {
      if (route.routeSLA.includes(departure)) {
        result = true;
      }
    });
    return result;
  };
  //number of records to be shown in metrics
  const filterWindow = (route) => {
    if (viewModelF.deliveryWindow.length === 0) {
      return true;
    }
    return viewModelF.deliveryWindow.includes(route.deliveryWindow);
  };

  const filterAnchorPoint = (route) => {
    if (viewModelF.sourceNode.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.sourceNode.forEach((sourceNode) => {
      if (route.sourceNode !== null && route.sourceNode.includes(sourceNode)) {
        result = true;
      }
    });
    return result;
  };

  const filterShipmentType = (route) => {
    if (viewModelF.shipmentType.length === 0) {
      return true;
    }
    let result = false;
    viewModelF.shipmentType.forEach((shipmentType) => {
      if (route.shipmentType.includes(shipmentType)) {
        result = true;
      }
    });
    return result;
  };

  routeList = routeList
    .filter((route) => filterCluster(route))
    .filter((route) => filterType(route))
    .filter((route) => filterDeparture(route))
    .filter((route) => filterWindow(route))
    .filter((route) => filterAnchorPoint(route))
    .filter((route) => filterShipmentType(route));

  const getMetricUri = (metric, capability) => {
    let filterObj = getRouteFilterByMetric(
      metric,
      viewModelF,
      outboundConfigModel.stationConfig,
      capability,
    );
    filterObj = {
      xptNodes: viewModelF.xptNodes,
      cluster: viewModelF.cluster,
      driverType: viewModelF.driverType,
      departure: viewModelF.departure,
      ...filterObj,
    };
    return JSON.stringify(filterObj).replace("/", "%2f");
  };

  const getAtRiskToolTipText = () => {
    if (outboundConfigModel.stationConfig.atRiskMetricV2) {
      return props.intl.formatMessage({
        id: "outboundAMZL.routeAtRiskWithStalledRoutesToolTip",
        defaultMessage:
          "High SPR routes which are at risk of missing dispatch time, or in progress routes " +
          "which do not have active picklists",
      });
    }
    return props.intl.formatMessage({
      id: "outboundAMZL.routeAtRiskToolTip",
      defaultMessage:
        "High SPR routes which are pending for pick and dispatch time is less " +
        "than SPR time threshold from now",
    });
  };

  const BoxContainer = ({ children }) => (
    <Box
      type="outline"
      spacingInset={isMobile ? "small" : "medium"}
      maxWidth="100%"
      overflowX="scroll"
    >
      {children}
    </Box>
  );

  const TotalRoutes = () => (
    <BoxContainer>
      <MetricViewV2
        data={{
          title: "outboundAMZL.totalRoutes",
          message: "Total Routes",
          value: routeList.length,
        }}
      />
    </BoxContainer>
  );

  const getNotAssignedMetricData = () => ({
    title: isStationTabularSSD
      ? "outboundSSD.stowed"
      : "outboundAMZL.notAssigned",
    message: isStationTabularSSD ? STOWED : "Not assigned",
    value: getRouteCountByStatus(ASSIGNABLE, routeList),
    targetTab: getTargetTabByMetric(ASSIGNABLE, capability.data),
    uri: getMetricUri(ASSIGNABLE, capability.data),
    tooltip: props.intl.formatMessage({
      id: "outboundAMZL.assignableToolTip",
      defaultMessage: "Routes which are not assigned yet",
    }),
  });

  const NotAssigned = () => (
    <BoxContainer>
      <ActionableMetricView data={getNotAssignedMetricData()} />
    </BoxContainer>
  );

  const AtRisk = () =>
    (!isStationARorSSD && !isSTAGWorkflowEnabled) || isStationTabularSSD ? (
      <BoxContainer>
        <ActionableMetricView
          data={{
            title: "outboundAMZL.routeAtRisk",
            message: ROUTES_AT_RISK,
            value: evaluateRouteAtRiskCount({
              routeList,
              isAtRiskMetricEnrichmentEnabled,
              isStationTabularSSD,
              cptTimeLimit: viewModelF.cptTimeLimit,
              sprPackageCount: viewModelF.sprPackageCount,
              sprTimeLimit: viewModelF.sprTimeLimit,
            }),
            targetTab: getTargetTabByMetric(ROUTES_AT_RISK),
            uri: getMetricUri(ROUTES_AT_RISK),
            tooltip: getAtRiskToolTipText(),
            textColor: isOverviewMetricEnrichmentEnabled
              ? "at-risk-number"
              : undefined,
          }}
        />
      </BoxContainer>
    ) : null;

  const DetailedStatus = () => (
    <BoxContainer>
      <ScrollableElementWrapper>
        <Row
          width="100%"
          spacing={isMobile ? "medium" : "large"}
          alignmentVertical="bottom"
        >
          {isAmxlStation ||
          isStationARorSSD ||
          isDrsPickStageVisibilityEnabled ? (
            <ActionableMetricView
              data={{
                title: isStationTabularSSD
                  ? "outboundSSD.inProgress"
                  : "outboundAMZL.pendingStow",
                message: isStationTabularSSD ? IN_PROGRESS : "Pending Stow",
                value: getRouteCountByStatus(NOT_READY, routeList),
                targetTab: getTargetTabByMetric(NOT_READY),
                uri: getRouteCodeListForStow(routeList),
                tooltip: props.intl.formatMessage({
                  id: "outboundAMZL.pendingStowToolTip",
                  defaultMessage: "Routes which are not completely stowed yet",
                }),
              }}
            />
          ) : null}
          {isOverviewMetricEnrichmentEnabled ? (
            <ActionableMetricView data={getNotAssignedMetricData()} />
          ) : null}
          {!isAmxlStation && !isStationARorSSD && !isSTAGWorkflowEnabled ? (
            <ActionableMetricView
              data={{
                title: "outboundAMZL.pendingPick",
                message: PENDING_PICK,
                value: getRouteCountByStatus(PENDING_PICK, routeList),
                targetTab: getTargetTabByMetric(PENDING_PICK),
                uri: getMetricUri(PENDING_PICK),
                tooltip: props.intl.formatMessage({
                  id: "outboundAMZL.pendingPickToolTip",
                  defaultMessage: "Routes which are not completely picked",
                }),
              }}
            />
          ) : null}
          {!isAmxlStation && (
            <ActionableMetricView
              data={{
                title: isStationTabularSSD
                  ? "outboundSSD.assigned"
                  : "outboundAMZL.pendingStage",
                message: isStationTabularSSD ? ASSIGNED : PENDING_STAGE,
                value: getRouteCountByStatus(PENDING_STAGE, routeList),
                targetTab: getTargetTabByMetric(PENDING_STAGE),
                uri: getMetricUri(PENDING_STAGE),
                tooltip: props.intl.formatMessage({
                  id: "outboundAMZL.pendingStageToolTip",
                  defaultMessage: "Routes which are picked but not staged",
                }),
              }}
            />
          )}
          {isSSDMMOTEnabled && (
            <ActionableMetricView
              data={{
                title: "outboundSSD.interimStaged",
                message: INTERIM_STAGED,
                value: getRouteCountByStatus(INTERIM_STAGED, routeList),
                targetTab: getTargetTabByMetric(INTERIM_STAGED),
                uri: getMetricUri(INTERIM_STAGED),
                tooltip: props.intl.formatMessage({
                  id: "outboundSSD.interimStageToolTip",
                  defaultMessage:
                    "Routes which are staged at Staging area and not Dock Door",
                }),
              }}
            />
          )}
          <ActionableMetricView
            data={{
              title: isStationTabularSSD
                ? "outboundSSD.staged"
                : "outboundAMZL.pendingDepart",
              message: isStationTabularSSD ? STAGED : PENDING_DEPART,
              value: getRouteCountByStatus(PENDING_DEPART, routeList),
              targetTab: getTargetTabByMetric(PENDING_DEPART),
              uri: getMetricUri(PENDING_DEPART),
              tooltip: props.intl.formatMessage({
                id: "outboundAMZL.pendingDepartToolTip",
                defaultMessage: "Routes which are staged but not departed",
              }),
            }}
          />
          <ActionableMetricView
            data={{
              title: "outboundAMZL.departed",
              message: DEPARTED,
              value: getRouteCountByStatus(DEPARTED, routeList),
              targetTab: getTargetTabByMetric(DEPARTED),
              uri: getMetricUri(DEPARTED),
              tooltip: props.intl.formatMessage({
                id: "outboundAMZL.departedToolTip",
                defaultMessage: "Routes which are departed",
              }),
            }}
          />
        </Row>
      </ScrollableElementWrapper>
    </BoxContainer>
  );

  return (
    <Column spacingInset={spacingInset_shorthand4} spacing="small">
      <RefreshTimer
        deps={[viewModel.lastUpdated]}
        lastUpdated={viewModel.lastUpdated}
      />
      <Responsive
        query="max-width"
        props={{
          items: {
            default: isOverviewMetricEnrichmentEnabled
              ? [<AtRisk />, <TotalRoutes />, <DetailedStatus />]
              : [
                  <TotalRoutes />,
                  <NotAssigned />,
                  <DetailedStatus />,
                  <AtRisk />,
                ],
            [SMALL]: isOverviewMetricEnrichmentEnabled
              ? [<AtRisk />, <TotalRoutes />, <DetailedStatus />]
              : [
                  <TotalRoutes />,
                  <NotAssigned />,
                  <AtRisk />,
                  <DetailedStatus />,
                ],
          },
          widths: {
            default: [],
            [SMALL]: ["35%", "35%", "30%", "100%"],
          },
          spacing: {
            default: "medium",
            [SMALL]: "small",
          },
        }}
      >
        {({ items, widths, spacing }) => (
          <Row
            spacing={spacing}
            maxWidth="100%"
            widths={widths}
            wrap="down"
            alignmentVertical="stretch"
          >
            {items}
          </Row>
        )}
      </Responsive>
    </Column>
  );
};

export default injectIntl(OutboundOverviewAMZLView);



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/OutboundOverviewAMZLView.js